import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useWorkspace } from "@/hooks/useWorkspace";
import { useIsMobile } from "@/hooks/use-mobile";
import { AppSeo } from "@/components/AppSeo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ResizableHandle, ResizablePanel, ResizablePanelGroup,
} from "@/components/ui/resizable";
import { toast } from "sonner";
import DOMPurify from "isomorphic-dompurify";
import {
  Mic, Plus, Search, Loader2, Check, Trash2, RefreshCw, Star, Archive, Sparkles,
  BookPlus, Quote,
} from "lucide-react";
import {
  scopeFilter,
  fetchSubscriptions, subscribeByFeedId, subscribeByFeedUrl, unsubscribe,
  syncSubscription, fetchEpisodes, fetchTranscript, transcribeEpisode,
  updateEpisode, fetchCategories, searchPodcasts, transcriptionCreditCost,
  languageLabel, TOP_LANGUAGES, ALL_LANGUAGES,
  previewEpisodesByFeedId, ensureEpisodeRow, ensureEpisodeRowStandalone,
  fetchEpisodeOverlayForSub, hasNewEpisode, markSubscriptionSeen,
  refreshLatestPublishedFromPreview, refreshAllLatestPublished,
  fetchNativeTranscript, saveNativeTranscript, promoteEpisodeToLibrary,
  episodeLibrarySourceId,
  type PodcastSubscription, type PodcastEpisode, type PodcastTranscript,
  type PodcastSearchResult, type PodcastCategory, type EpisodeFilter,
  type PodcastPreviewEpisode, type NativeTranscriptResult,
} from "@/lib/podcasts";
import { tierEntitlements } from "@/lib/tier";

const LANG_STORAGE = "excerptus.podcasts.discover.language";
const CAT_STORAGE = "excerptus.podcasts.discover.category";

const formatDuration = (secs: number | null): string => {
  if (!secs || secs < 1) return "—";
  const h = Math.floor(secs / 3600), m = Math.floor((secs % 3600) / 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const formatDate = (iso: string | null): string => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
};

const MyPodcasts = () => {
  const { active, workspaces, libraryMode, loading: wsLoading } = useWorkspace();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const scopeIds = useMemo(
    () => scopeFilter(libraryMode, active, workspaces),
    [libraryMode, active, workspaces],
  );

  const [tab, setTab] = useState<"subscribed" | "discover">("subscribed");
  const [subs, setSubs] = useState<PodcastSubscription[]>([]);
  const [episodes, setEpisodes] = useState<PodcastEpisode[]>([]);
  const [filter, setFilter] = useState<EpisodeFilter>("starred");
  const [selectedSubId, setSelectedSubId] = useState<string | null>(null);
  const [selectedEpId, setSelectedEpId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Discover state
  const [categories, setCategories] = useState<PodcastCategory[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchCat, setSearchCat] = useState<string>(() => localStorage.getItem(CAT_STORAGE) || "all");
  const [searchLang, setSearchLang] = useState<string>(
    () => localStorage.getItem(LANG_STORAGE) || (navigator.language?.slice(0, 2) ?? "en"),
  );
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState<PodcastSearchResult[]>([]);
  const [addUrlOpen, setAddUrlOpen] = useState(false);
  const [addUrl, setAddUrl] = useState("");

  // Transcript state
  const [transcript, setTranscript] = useState<PodcastTranscript | null>(null);
  const [transcribing, setTranscribing] = useState(false);
  // Live (non-persisted) native transcript loaded from the publisher feed.
  const [nativeLoading, setNativeLoading] = useState(false);
  const [nativeResult, setNativeResult] = useState<NativeTranscriptResult | null>(null);
  const [nativeSaving, setNativeSaving] = useState(false);

  // Feed-preview state (used by BOTH Discover preview AND Subscribed click)
  const [previewFeed, setPreviewFeed] = useState<PodcastSearchResult | null>(null);
  const [previewSource, setPreviewSource] = useState<"discover" | "subscribed" | null>(null);
  const [previewEpisodes, setPreviewEpisodes] = useState<PodcastPreviewEpisode[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [selectedPreviewGuid, setSelectedPreviewGuid] = useState<string | null>(null);
  const [previewPageSize, setPreviewPageSize] = useState(20);
  const [previewLoadingMore, setPreviewLoadingMore] = useState(false);
  // DB-row overlay for the currently-selected Subscribed feed, keyed by
  // PodcastIndex episode id (string) AND by guid for fallback lookups.
  const [subOverlay, setSubOverlay] = useState<Map<string, PodcastEpisode>>(new Map());
  const PREVIEW_PAGE_STEPS = [20, 50, 100, 200, 500, 1000];

  const tier = active?.account_tier ?? null;
  const cap = tierEntitlements(tier).podcastSubscriptionsMax;
  const activeCount = active ? subs.filter((s) => s.workspace_id === active.id).length : 0;
  const atCap = activeCount >= cap;

  // Load subscriptions
  const refreshSubs = useCallback(async () => {
    if (!scopeIds.length) { setSubs([]); return; }
    try {
      const data = await fetchSubscriptions(scopeIds);
      setSubs(data);
    } catch (e) { toast.error((e as Error).message); }
  }, [scopeIds]);

  useEffect(() => { void refreshSubs(); }, [refreshSubs]);

  // Page-load "new episode" check — fetches max=1 from PI for each sub
  // (cached server-side for 5 min) and updates latest_episode_published_at.
  // Triggers a badge re-render via setSubs.
  useEffect(() => {
    if (subs.length === 0) return;
    let cancelled = false;
    void refreshAllLatestPublished(subs).then((updated) => {
      if (cancelled) return;
      // Only update if at least one row actually changed timestamps.
      const changed = updated.some(
        (s, i) => s.latest_episode_published_at !== subs[i]?.latest_episode_published_at,
      );
      if (changed) setSubs(updated);
    });
    return () => { cancelled = true; };
    // Intentionally only run when the *set* of sub ids changes, not on
    // every render — otherwise this would loop on the setSubs above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subs.map((s) => s.id).join(",")]);

  // Auto-flip to Discover if empty workspace
  useEffect(() => {
    if (!wsLoading && subs.length === 0 && tab === "subscribed") setTab("discover");
  }, [wsLoading, subs.length, tab]);

  // Load DB episodes for the aggregate views ("All podcasts" with filter
  // chips). When a specific sub is selected we render the live feed
  // instead (see openSubPreview) — so skip this fetch in that case.
  useEffect(() => {
    if (!scopeIds.length) { setEpisodes([]); return; }
    if (selectedSubId) { setEpisodes([]); return; }
    setLoading(true);
    fetchEpisodes({ workspaceIds: scopeIds, filter, subscriptionId: null })
      .then(setEpisodes)
      .catch((e) => toast.error((e as Error).message))
      .finally(() => setLoading(false));
  }, [scopeIds, filter, selectedSubId]);

  // Load categories once (with error surfacing + manual refresh)
  const loadCategories = useCallback((notify = false) => {
    fetchCategories()
      .then((cats) => {
        setCategories(cats);
        if (notify) toast.success(`Loaded ${cats.length} categories`);
        if (cats.length === 0 && notify) {
          toast.error("PodcastIndex returned no categories — check API keys");
        }
      })
      .catch((e) => toast.error(`Couldn't load podcast categories: ${(e as Error).message}`));
  }, []);
  useEffect(() => { loadCategories(false); }, [loadCategories]);

  // Load saved transcript when episode selected
  useEffect(() => {
    if (!selectedEpId) { setTranscript(null); return; }
    fetchTranscript(selectedEpId).then(setTranscript).catch(() => setTranscript(null));
  }, [selectedEpId]);

  // Reset native transcript whenever the chosen episode changes.
  useEffect(() => {
    setNativeResult(null);
    setNativeLoading(false);
  }, [selectedEpId, selectedPreviewGuid]);

  // Auto-fetch the publisher's native <podcast:transcript> when an episode
  // is opened and no saved transcript exists yet. Pure read — never writes.
  useEffect(() => {
    if (transcript) return; // saved version wins
    if (!selectedEpId && !selectedPreviewGuid) return;
    const piId = selectedPreviewEp?.podcastindex_episode_id
      ?? episodes.find((e) => e.id === selectedEpId)?.podcastindex_episode_id
      ?? null;
    if (!piId) return;
    let cancelled = false;
    setNativeLoading(true);
    fetchNativeTranscript(piId)
      .then((r) => { if (!cancelled) setNativeResult(r); })
      .catch(() => { if (!cancelled) setNativeResult({ available: false, reason: "error" }); })
      .finally(() => { if (!cancelled) setNativeLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEpId, selectedPreviewGuid, transcript?.episode_id]);



  const selectedEp = episodes.find((e) => e.id === selectedEpId) || null;
  const selectedSub = selectedEp
    ? subs.find((s) => s.id === selectedEp.subscription_id) || null
    : (selectedSubId ? subs.find((s) => s.id === selectedSubId) || null : null);
  const selectedPreviewEp = selectedPreviewGuid
    ? previewEpisodes.find((e) => (e.guid ?? String(e.podcastindex_episode_id)) === selectedPreviewGuid) || null
    : null;
  // Both Discover preview AND Subscribed-with-sub-selected use the live
  // feed list. Aggregate views (no sub selected) use the DB-backed list.
  const isPreviewMode = !!previewFeed;

  // Unified "currently playing" view-model so the player pane works for both modes.
  // In Subscribed-preview mode, when the user has clicked a feed episode we
  // also materialize a DB row (selectedEp) so star/archive/transcribe work.
  const playing = selectedPreviewEp
    ? {
        kind: (previewSource === "subscribed" ? "subscribed" : "preview") as "subscribed" | "preview",
        title: selectedPreviewEp.title,
        image_url: selectedPreviewEp.image_url ?? previewFeed?.image_url ?? null,
        audio_url: selectedPreviewEp.audio_url,
        description: selectedPreviewEp.description,
        published_at: selectedPreviewEp.published_at,
        duration_seconds: selectedPreviewEp.duration_seconds,
        sourceTitle: previewFeed?.title ?? "",
      }
    : selectedEp
    ? {
        kind: "subscribed" as const,
        title: selectedEp.title,
        image_url: selectedEp.image_url ?? selectedSub?.image_url ?? null,
        audio_url: selectedEp.audio_url,
        description: selectedEp.description,
        published_at: selectedEp.published_at,
        duration_seconds: selectedEp.duration_seconds,
        sourceTitle: selectedSub?.title ?? "",
      }
    : null;

  // PodcastIndex episode id for whatever is currently playing (preferred
  // source for native-transcript lookup).
  const currentPiEpisodeId: number | null =
    selectedPreviewEp?.podcastindex_episode_id
    ?? selectedEp?.podcastindex_episode_id
    ?? null;

  // Show metadata for the currently-playing episode — used by Add to
  // Library to build the citation row. Pulled from previewFeed when
  // available, falling back to the subscription row.
  const currentShow: PodcastSearchResult | null =
    previewFeed
      ? previewFeed
      : selectedSub
        ? {
            feed_id: selectedSub.podcastindex_feed_id,
            feed_url: selectedSub.feed_url,
            title: selectedSub.title,
            author: selectedSub.author,
            description: selectedSub.description,
            image_url: selectedSub.image_url,
            language: selectedSub.language,
            categories: (selectedSub.categories as string[] | null) ?? [],
            episode_count: null,
          }
        : null;

  // ---- "Already in My Library?" lookup for the currently-playing episode.
  // Uses the same source_id derivation as promoteEpisodeToLibrary so the
  // button reflects the real DB state, not just the most recent click.
  const playingLibrarySourceId: string | null =
    currentPiEpisodeId
      ? String(currentPiEpisodeId)
      : (selectedPreviewEp?.guid ?? (selectedEp ? episodeLibrarySourceId(selectedEp) : null));
  const [episodeLibraryItemId, setEpisodeLibraryItemId] = useState<string | null>(null);
  useEffect(() => {
    if (!active?.id || !playingLibrarySourceId) { setEpisodeLibraryItemId(null); return; }
    let cancelled = false;
    (async () => {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data } = await supabase
        .from("library_items")
        .select("id")
        .eq("workspace_id", active.id)
        .eq("source_provider", "podcastindex")
        .eq("source_id", playingLibrarySourceId)
        .maybeSingle();
      if (!cancelled) setEpisodeLibraryItemId(data?.id ?? null);
    })();
    return () => { cancelled = true; };
  }, [active?.id, playingLibrarySourceId]);





  // Convert a subscription into the PodcastSearchResult shape used by previewFeed.
  const subToFeed = (s: PodcastSubscription): PodcastSearchResult => ({
    feed_id: s.podcastindex_feed_id,
    feed_url: s.feed_url,
    title: s.title,
    author: s.author,
    description: s.description,
    image_url: s.image_url,
    language: s.language,
    categories: (s.categories as string[] | null) ?? [],
    episode_count: null, // enriched from PI on fetch
  });

  // Reset all preview/feed-view state.
  const resetPreviewState = () => {
    setPreviewFeed(null);
    setPreviewSource(null);
    setPreviewEpisodes([]);
    setSelectedPreviewGuid(null);
    setPreviewPageSize(20);
    setSubOverlay(new Map());
  };

  // Open a preview for a Discover result.
  const openPreview = async (r: PodcastSearchResult) => {
    setPreviewFeed(r);
    setPreviewSource("discover");
    setSelectedSubId(null);
    setSelectedEpId(null);
    setSelectedPreviewGuid(null);
    setPreviewEpisodes([]);
    setSubOverlay(new Map());
    setPreviewPageSize(20);
    setPreviewLoading(true);
    try {
      const { episodes, feed } = await previewEpisodesByFeedId(r.feed_id, 20);
      setPreviewEpisodes(episodes);
      if (feed) setPreviewFeed((prev) => (prev ? { ...prev, ...feed } : feed));
    } catch (e) { toast.error((e as Error).message); }
    finally { setPreviewLoading(false); }
  };

  // Open a "preview" for a Subscribed podcast — same UX as Discover, but
  // overlays per-episode DB state and lazily mints rows on interaction.
  // Also stamps last_seen_at = now() so the "new episode" badge clears.
  const openSubPreview = async (sub: PodcastSubscription) => {
    const asFeed = subToFeed(sub);
    setPreviewFeed(asFeed);
    setPreviewSource("subscribed");
    setSelectedSubId(sub.id);
    setSelectedEpId(null);
    setSelectedPreviewGuid(null);
    setPreviewEpisodes([]);
    setPreviewPageSize(20);
    setPreviewLoading(true);
    // Optimistically clear badge in UI.
    const seenIso = new Date().toISOString();
    setSubs((prev) =>
      prev.map((s) => (s.id === sub.id ? { ...s, last_seen_at: seenIso } : s)),
    );
    void markSubscriptionSeen(sub.id).catch(() => { /* best-effort */ });
    try {
      const [{ episodes, feed }, overlay] = await Promise.all([
        previewEpisodesByFeedId(sub.podcastindex_feed_id, 20),
        fetchEpisodeOverlayForSub(sub.id).catch(() => [] as PodcastEpisode[]),
      ]);
      setPreviewEpisodes(episodes);
      if (feed) setPreviewFeed((prev) => (prev ? { ...prev, ...feed } : feed));
      // Refresh latest_episode_published_at from the freshly-loaded list.
      void refreshLatestPublishedFromPreview(sub.id, episodes).then((latest) => {
        if (latest) {
          setSubs((prev) =>
            prev.map((s) => (s.id === sub.id ? { ...s, latest_episode_published_at: latest } : s)),
          );
        }
      });
      const map = new Map<string, PodcastEpisode>();
      for (const row of overlay) {
        if (row.podcastindex_episode_id) map.set(`pi:${row.podcastindex_episode_id}`, row);
        if (row.guid) map.set(`guid:${row.guid}`, row);
      }
      setSubOverlay(map);
      setEpisodes(overlay);
    } catch (e) { toast.error((e as Error).message); }
    finally { setPreviewLoading(false); }
  };

  // Look up a DB row for a given feed episode (via overlay map).
  const overlayFor = (ep: PodcastPreviewEpisode): PodcastEpisode | null => {
    if (ep.podcastindex_episode_id) {
      const r = subOverlay.get(`pi:${ep.podcastindex_episode_id}`);
      if (r) return r;
    }
    if (ep.guid) {
      const r = subOverlay.get(`guid:${ep.guid}`);
      if (r) return r;
    }
    return null;
  };

  // When the user clicks a preview episode in Subscribed mode, also
  // materialize/load the DB row so star/archive/transcribe wire up.
  const selectPreviewEpisode = async (ep: PodcastPreviewEpisode) => {
    const key = ep.guid ?? String(ep.podcastindex_episode_id);
    setSelectedPreviewGuid(key);
    if (previewSource !== "subscribed" || !selectedSubId) return;
    const sub = subs.find((s) => s.id === selectedSubId);
    if (!sub) return;
    try {
      const row = await ensureEpisodeRow(sub, ep);
      // Update overlay + episodes array, and select it.
      setSubOverlay((prev) => {
        const next = new Map(prev);
        if (row.podcastindex_episode_id) next.set(`pi:${row.podcastindex_episode_id}`, row);
        if (row.guid) next.set(`guid:${row.guid}`, row);
        return next;
      });
      setEpisodes((prev) => {
        const without = prev.filter((e) => e.id !== row.id);
        return [...without, row];
      });
      setSelectedEpId(row.id);
    } catch (e) {
      console.warn("[podcasts] ensureEpisodeRow failed:", (e as Error).message);
    }
  };

  const loadMorePreview = async () => {
    if (!previewFeed) return;
    const idx = PREVIEW_PAGE_STEPS.indexOf(previewPageSize);
    const next = PREVIEW_PAGE_STEPS[Math.min(idx + 1, PREVIEW_PAGE_STEPS.length - 1)];
    if (next === previewPageSize) return;
    setPreviewLoadingMore(true);
    try {
      const { episodes, feed } = await previewEpisodesByFeedId(previewFeed.feed_id, next);
      setPreviewEpisodes(episodes);
      if (feed) setPreviewFeed((prev) => (prev ? { ...prev, ...feed } : feed));
      setPreviewPageSize(next);
    } catch (e) { toast.error((e as Error).message); }
    finally { setPreviewLoadingMore(false); }
  };

  // Reset preview when switching tabs.
  const handleTabChange = (v: "subscribed" | "discover") => {
    setTab(v);
    resetPreviewState();
    setSelectedSubId(null);
    setSelectedEpId(null);
  };


  // ---- Discover search ----
  const hasCat = searchCat && searchCat !== "all";
  const canSearch = !!searchTerm.trim() || !!hasCat;
  const isTrendingMode = !searchTerm.trim() && !!hasCat;
  const trendingCatName = useMemo(() => {
    if (!hasCat) return null;
    const c = categories.find((c) => String(c.id) === searchCat);
    return c?.name ?? null;
  }, [hasCat, searchCat, categories]);
  const [lastQueryWasTrending, setLastQueryWasTrending] = useState(false);
  const [lastTrendingCatName, setLastTrendingCatName] = useState<string | null>(null);

  const runSearch = async () => {
    if (!canSearch) return;
    setSearching(true);
    const wasTrending = isTrendingMode;
    try {
      const r = await searchPodcasts({
        term: searchTerm.trim() || undefined,
        cat: hasCat ? searchCat : undefined,
        lang: searchLang && searchLang !== "any" ? searchLang : undefined,
        max: 40,
      });
      setResults(r);
      setLastQueryWasTrending(wasTrending);
      setLastTrendingCatName(wasTrending ? trendingCatName : null);
      if (r.length === 0) {
        const langName = searchLang === "any" ? "any language" : languageLabel(searchLang);
        if (wasTrending) {
          toast(`No trending podcasts found in ${trendingCatName ?? "this category"} (${langName}).`);
        } else {
          toast(`No podcasts found for that term in ${langName}.`);
        }
      }
    } catch (e) { toast.error((e as Error).message); }
    finally { setSearching(false); }
  };


  const handleAdd = async (feedId: number) => {
    if (!active) return;
    if (atCap) { toast.error(`At cap (${cap}). Remove a podcast or upgrade.`); return; }
    setBusyId(String(feedId));
    try {
      const r = await subscribeByFeedId(active.id, feedId);
      toast.success(`Subscribed to ${r.subscription.title}`);
      await refreshSubs();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  };

  const handleAddByUrl = async () => {
    if (!active || !addUrl.trim()) return;
    setBusyId("url");
    try {
      const r = await subscribeByFeedUrl(active.id, addUrl.trim());
      toast.success(`Subscribed to ${r.subscription.title}`);
      setAddUrl(""); setAddUrlOpen(false);
      await refreshSubs();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  };

  const handleUnsubscribe = async (id: string) => {
    setBusyId(id);
    try {
      await unsubscribe(id);
      toast.success("Unsubscribed");
      if (selectedSubId === id) setSelectedSubId(null);
      await refreshSubs();
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  };

  const handleSync = async (id: string) => {
    setBusyId(id);
    try {
      const r = await syncSubscription(id);
      // Update the sub row in place with the fresh latest timestamp so
      // the "new" badge updates without a full refetch.
      if (r.latest_episode_published_at) {
        setSubs((prev) =>
          prev.map((s) =>
            s.id === id
              ? { ...s, latest_episode_published_at: r.latest_episode_published_at ?? s.latest_episode_published_at, last_synced_at: new Date().toISOString() }
              : s,
          ),
        );
      }
      toast.success(
        r.new_items > 0
          ? `${r.new_items} new episode${r.new_items === 1 ? "" : "s"}`
          : "Up to date",
      );
      // refresh overlay if we're viewing this sub
      if (selectedSubId === id) {
        const overlay = await fetchEpisodeOverlayForSub(id).catch(() => [] as PodcastEpisode[]);
        const map = new Map<string, PodcastEpisode>();
        for (const row of overlay) {
          if (row.podcastindex_episode_id) map.set(`pi:${row.podcastindex_episode_id}`, row);
          if (row.guid) map.set(`guid:${row.guid}`, row);
        }
        setSubOverlay(map);
        setEpisodes(overlay);
      }
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  };

  const handleTranscribe = async () => {
    if (!selectedEp) return;
    setTranscribing(true);
    try {
      const r = await transcribeEpisode(selectedEp.id);
      setTranscript(r.transcript);
      if (r.cost_credits > 0) toast.success(`Transcribed (${r.cost_credits} credits)`);
      else toast.success("Transcript loaded from cache");
    } catch (e) { toast.error((e as Error).message); }
    finally { setTranscribing(false); }
  };

  const handleEpisodeAction = async (
    ep: PodcastEpisode,
    patch: Partial<Pick<PodcastEpisode, "is_read" | "is_archived" | "is_starred">>,
  ) => {
    const merged = { ...ep, ...patch };
    setEpisodes((prev) => prev.map((e) => e.id === ep.id ? merged : e));
    setSubOverlay((prev) => {
      const next = new Map(prev);
      if (merged.podcastindex_episode_id) next.set(`pi:${merged.podcastindex_episode_id}`, merged);
      if (merged.guid) next.set(`guid:${merged.guid}`, merged);
      return next;
    });
    try { await updateEpisode(ep.id, patch); }
    catch (e) { toast.error((e as Error).message); }
  };

  // Core "ensure this episode is in My Library" — used by both the explicit
  // Add-to-My-Library button (toasts on success) and by the silent
  // create-excerpt-from-selection flow (no toast on this step).
  const ensureEpisodeInLibrary = async (): Promise<
    { library_item_id: string; created: boolean; epRow: PodcastEpisode } | null
  > => {
    if (!active) return null;
    const piEpId = currentPiEpisodeId;
    if (!piEpId && !selectedEp) { toast.error("No episode selected"); return null; }
    const show = currentShow;
    if (!show) { toast.error("Missing podcast metadata"); return null; }
    let epRow: PodcastEpisode | null = selectedEp;
    if (!epRow) {
      if (previewSource === "subscribed" && selectedSubId && selectedPreviewEp) {
        const sub = subs.find((s) => s.id === selectedSubId);
        if (sub) epRow = await ensureEpisodeRow(sub, selectedPreviewEp);
      } else if (selectedPreviewEp) {
        epRow = await ensureEpisodeRowStandalone(active.id, selectedPreviewEp);
      }
    }
    if (!epRow) throw new Error("Could not materialize episode row");
    const r = await promoteEpisodeToLibrary(active.id, epRow, {
      title: show.title,
      author: show.author,
      feed_url: show.feed_url,
      image_url: show.image_url,
      language: show.language,
      podcastindex_feed_id: show.feed_id ?? null,
    });
    if (nativeResult?.available && nativeResult.transcript && !transcript) {
      try {
        const saved = await saveNativeTranscript(
          epRow.id, active.id, nativeResult.transcript,
          nativeResult.duration_seconds ?? epRow.duration_seconds ?? null,
        );
        setTranscript(saved);
      } catch (e) {
        console.warn("[podcasts] auto-save native on promote failed:", (e as Error).message);
      }
    }
    setEpisodeLibraryItemId(r.library_item_id);
    return { library_item_id: r.library_item_id, created: r.created, epRow };
  };

  // Episode-level "Add to My Library" — works whether the episode is
  // subscribed or one-off (Discover). User-facing version with a toast.
  const handlePromoteEpisode = async () => {
    setBusyId("promote-ep");
    try {
      const r = await ensureEpisodeInLibrary();
      if (!r) return;
      toast.success(r.created ? "Episode added to My Library" : "Already in My Library");
    } catch (e) { toast.error((e as Error).message); }
    finally { setBusyId(null); }
  };



  const handleSaveNative = async () => {
    if (!active || !nativeResult?.available || !nativeResult.transcript) return;
    const piEpId = currentPiEpisodeId;
    if (!piEpId && !selectedEp) return;
    setNativeSaving(true);
    try {
      let epRow: PodcastEpisode | null = selectedEp;
      if (!epRow) {
        if (previewSource === "subscribed" && selectedSubId && selectedPreviewEp) {
          const sub = subs.find((s) => s.id === selectedSubId);
          if (sub) epRow = await ensureEpisodeRow(sub, selectedPreviewEp);
        } else if (selectedPreviewEp) {
          epRow = await ensureEpisodeRowStandalone(active.id, selectedPreviewEp);
        }
      }
      if (!epRow) throw new Error("Could not materialize episode row");
      const saved = await saveNativeTranscript(
        epRow.id, active.id, nativeResult.transcript,
        nativeResult.duration_seconds ?? epRow.duration_seconds ?? null,
      );
      setTranscript(saved);
      toast.success("Transcript saved");
    } catch (e) { toast.error((e as Error).message); }
    finally { setNativeSaving(false); }
  };


  // ---- Audio + word-level transcript sync ----
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [audioTime, setAudioTime] = useState(0);

  useEffect(() => { setAudioTime(0); }, [selectedEpId, selectedPreviewGuid]);

  // Word-timing for the currently-rendered transcript. Saved DB rows use
  // `{ word, start, end }` (Deepgram) OR could be native-saved (same
  // shape). Live native results use the same shape but from nativeResult.
  type TWord = { word: string; punctuated_word?: string; start: number; end: number };
  const transcriptWords = useMemo<TWord[] | null>(() => {
    if (transcript?.words) {
      const w = transcript.words as unknown as TWord[];
      return Array.isArray(w) ? w : null;
    }
    if (nativeResult?.available && nativeResult.transcript?.words) {
      return nativeResult.transcript.words.map((w) => ({ word: w.word, start: w.start, end: w.end }));
    }
    return null;
  }, [transcript, nativeResult]);


  const transcriptText = transcript?.text
    ?? (nativeResult?.available ? nativeResult.transcript?.text ?? null : null);
  const isNativeUnsaved = !transcript && !!nativeResult?.available && !!nativeResult.transcript;


  const seekTo = (sec: number) => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = sec;
    void a.play().catch(() => {});
  };

  // ---- Transcript selection → Create excerpt ----
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);
  const [selection, setSelection] = useState<
    { text: string; startSec: number | null; top: number; left: number } | null
  >(null);
  const [creatingExcerpt, setCreatingExcerpt] = useState(false);

  const updateSelectionFromDom = useCallback(() => {
    const container = transcriptContainerRef.current;
    const sel = typeof window !== "undefined" ? window.getSelection() : null;
    if (!container || !sel || sel.rangeCount === 0 || sel.isCollapsed) {
      setSelection(null);
      return;
    }
    const range = sel.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) {
      setSelection(null);
      return;
    }
    const text = sel.toString().trim();
    if (text.length < 2) { setSelection(null); return; }
    // Find first selected word span to recover start timestamp.
    let startSec: number | null = null;
    const startNode = range.startContainer.nodeType === Node.TEXT_NODE
      ? range.startContainer.parentElement
      : (range.startContainer as HTMLElement);
    let span: HTMLElement | null = startNode;
    while (span && span !== container && !span.dataset?.wordStart) {
      span = span.parentElement;
    }
    if (span && span.dataset?.wordStart) {
      const n = Number(span.dataset.wordStart);
      if (Number.isFinite(n)) startSec = Math.floor(n);
    }
    const rect = range.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    setSelection({
      text,
      startSec,
      top: rect.top - containerRect.top - 36,
      left: rect.left - containerRect.left + rect.width / 2,
    });
  }, []);

  useEffect(() => {
    const onSelChange = () => updateSelectionFromDom();
    document.addEventListener("selectionchange", onSelChange);
    return () => document.removeEventListener("selectionchange", onSelChange);
  }, [updateSelectionFromDom]);

  const handleCreateExcerptFromSelection = async () => {
    if (!selection || !active) return;
    setCreatingExcerpt(true);
    try {
      const r = await ensureEpisodeInLibrary();
      if (!r) return;
      const audioUrl = playing?.audio_url ?? "";
      const sourceUrl = audioUrl
        ? (selection.startSec != null ? `${audioUrl}#t=${selection.startSec}` : audioUrl)
        : "";
      const params = new URLSearchParams();
      params.set("libraryItem", r.library_item_id);
      params.set("prefillBody", selection.text);
      if (sourceUrl) params.set("prefillSourceUrl", sourceUrl);
      params.set("returnTo", "/podcasts");
      // Clear DOM selection so floating button disappears.
      window.getSelection()?.removeAllRanges();
      setSelection(null);
      navigate(`/new-excerpt?${params.toString()}`);
    } catch (e) { toast.error((e as Error).message); }
    finally { setCreatingExcerpt(false); }
  };



  const persistCat = (v: string) => { setSearchCat(v); localStorage.setItem(CAT_STORAGE, v); };
  const persistLang = (v: string) => { setSearchLang(v); localStorage.setItem(LANG_STORAGE, v); };

  // ---- Panes ----

  const LeftPane = (
    <div className="h-full flex flex-col">
      <div className="shrink-0 border-b border-border/60 px-3 pt-3 pb-2">
        <Tabs value={tab} onValueChange={(v) => handleTabChange(v as "subscribed" | "discover")}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="subscribed">Subscribed ({activeCount}/{cap})</TabsTrigger>
            <TabsTrigger value="discover">Discover</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {tab === "subscribed" ? (
        <ScrollArea className="flex-1 min-h-0">
          <div className="p-2 space-y-1">
            <button
              onClick={() => { setSelectedSubId(null); resetPreviewState(); }}
              className={`w-full text-left px-3 py-2 rounded text-sm transition-shadow hover:ring-1 hover:ring-primary/40 ${!selectedSubId ? "ring-2 ring-primary" : ""}`}
            >
              All podcasts
            </button>
            {subs.map((s) => {
              const isNew = hasNewEpisode(s);
              return (
              <div key={s.id} className={`group flex items-start gap-2 px-2 py-2 rounded transition-shadow hover:ring-1 hover:ring-primary/40 ${selectedSubId === s.id ? "ring-2 ring-primary" : ""}`}>
                <button onClick={() => void openSubPreview(s)} className="flex-1 flex items-start gap-2 text-left min-w-0">
                  <div className="relative shrink-0">
                    {s.image_url ? (
                      <img src={s.image_url} alt="" className="h-10 w-10 rounded object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-muted flex items-center justify-center">
                        <Mic className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    {isNew && (
                      <span
                        className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-background"
                        aria-label="New episode"
                        title="New episode available"
                      />
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className={`text-sm truncate ${isNew ? "font-semibold" : "font-medium"}`}>{s.title}</div>
                    {s.author && <div className="text-xs text-muted-foreground truncate">{s.author}</div>}
                  </div>
                </button>
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleSync(s.id)} disabled={busyId === s.id} title="Sync">
                    {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                  </Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6 text-destructive" onClick={() => handleUnsubscribe(s.id)} disabled={busyId === s.id} title="Unsubscribe">
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
              );
            })}
            {subs.length === 0 && (
              <div className="p-4 text-sm text-muted-foreground text-center">
                No subscriptions yet. Use Discover to find podcasts.
              </div>
            )}
          </div>
        </ScrollArea>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col">
          <div className="shrink-0 p-3 space-y-2 border-b border-border/60">
            <div className="flex gap-2">
              <Select value={searchCat} onValueChange={persistCat}>
                <SelectTrigger className="h-9"><SelectValue placeholder="All categories" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All categories</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="ghost"
                className="h-9 w-9 p-0 shrink-0"
                title="Refresh categories"
                onClick={() => loadCategories(true)}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
              <Select value={searchLang} onValueChange={persistLang}>
                <SelectTrigger className="h-9 w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any language</SelectItem>
                  {TOP_LANGUAGES.map((l) => (
                    <SelectItem key={`top-${l.code}`} value={l.code}>{l.label}</SelectItem>
                  ))}
                  <SelectItem value="---" disabled>──────────</SelectItem>
                  {ALL_LANGUAGES
                    .filter((l) => !TOP_LANGUAGES.some((t) => t.code === l.code))
                    .map((l) => (
                      <SelectItem key={`all-${l.code}`} value={l.code}>{l.label}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder={isTrendingMode ? `Top in ${trendingCatName ?? "category"} — or type to search…` : "Search podcasts..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") void runSearch(); }}
                className="h-9"
              />
              <Button size="sm" onClick={runSearch} disabled={searching || !canSearch} title={isTrendingMode ? `Browse top in ${trendingCatName ?? "category"}` : "Search"}>
                {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              </Button>
            </div>
            {addUrlOpen ? (
              <div className="flex gap-2">
                <Input
                  placeholder="https://example.com/feed.xml"
                  value={addUrl}
                  onChange={(e) => setAddUrl(e.target.value)}
                  className="h-8 text-xs"
                />
                <Button size="sm" variant="secondary" onClick={handleAddByUrl} disabled={busyId === "url"}>Add</Button>
                <Button size="sm" variant="ghost" onClick={() => { setAddUrlOpen(false); setAddUrl(""); }}>Cancel</Button>
              </div>
            ) : (
              <button onClick={() => setAddUrlOpen(true)} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2">
                Add by RSS URL
              </button>
            )}
          </div>
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-2 space-y-2">
              {results.length > 0 && lastQueryWasTrending && lastTrendingCatName && (
                <div className="px-1 pb-1 text-xs uppercase tracking-wide text-muted-foreground">
                  Top in {lastTrendingCatName}
                </div>
              )}
              {results.map((r) => {
                const subscribed = subs.some((s) => s.podcastindex_feed_id === r.feed_id);
                const isPreviewing = previewFeed?.feed_id === r.feed_id;
                return (
                  <button
                    key={r.feed_id}
                    type="button"
                    onClick={() => openPreview(r)}
                    className={`w-full text-left flex items-start gap-2 p-2 rounded border border-border/60 transition-shadow hover:ring-1 hover:ring-primary/40 ${isPreviewing ? "ring-2 ring-primary" : ""}`}
                  >
                    {r.image_url ? (
                      <img src={r.image_url} alt="" className="h-12 w-12 rounded object-cover shrink-0" />
                    ) : (
                      <div className="h-12 w-12 rounded bg-muted shrink-0 flex items-center justify-center">
                        <Mic className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{r.title}</div>
                      {r.author && <div className="text-xs text-muted-foreground truncate">{r.author}</div>}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {r.language && <Badge variant="outline" className="h-4 text-[10px] px-1">{r.language.toUpperCase().slice(0, 2)}</Badge>}
                        {r.categories.slice(0, 4).map((c) => (
                          <Badge key={c} variant="secondary" className="h-4 text-[10px] px-1">{c}</Badge>
                        ))}
                        {r.categories.length > 4 && (
                          <Badge variant="secondary" className="h-4 text-[10px] px-1" title={r.categories.slice(4).join(", ")}>+{r.categories.length - 4}</Badge>
                        )}
                        {typeof r.episode_count === "number" && r.episode_count > 0 && (
                          <Badge variant="outline" className="h-4 text-[10px] px-1">{r.episode_count} eps</Badge>
                        )}
                      </div>
                    </div>
                    {subscribed ? (
                      <Badge variant="default" className="shrink-0"><Check className="h-3 w-3 mr-1" />Subscribed</Badge>
                    ) : (
                      <Button
                        asChild
                        size="sm"
                        variant="outline"
                        disabled={busyId === String(r.feed_id) || atCap}
                        title={atCap ? `At cap (${cap})` : "Subscribe"}
                      >
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); if (!atCap && busyId !== String(r.feed_id)) void handleAdd(r.feed_id); }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.stopPropagation();
                              e.preventDefault();
                              if (!atCap && busyId !== String(r.feed_id)) void handleAdd(r.feed_id);
                            }
                          }}
                        >
                          {busyId === String(r.feed_id) ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                        </span>
                      </Button>
                    )}
                  </button>
                );
              })}
              {results.length === 0 && !searching && (
                <div className="p-4 text-sm text-muted-foreground text-center">
                  Pick a category to browse the top podcasts, or type a search term.
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )}
    </div>
  );

  const EpisodesPane = (
    <div className="h-full flex flex-col">
      <div className="shrink-0 border-b border-border/60 px-3 py-2">
        {isPreviewMode ? (
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-muted-foreground">{previewSource === "subscribed" ? "Episodes" : "Preview"}</div>
              <div className="text-sm font-medium truncate">{previewFeed?.title}</div>
            </div>
            {previewFeed && subs.some((s) => s.podcastindex_feed_id === previewFeed.feed_id) ? (
              <Badge variant="default" className="shrink-0"><Check className="h-3 w-3 mr-1" />Subscribed</Badge>
            ) : (
              <Button
                size="sm"
                variant="default"
                onClick={() => previewFeed && handleAdd(previewFeed.feed_id)}
                disabled={!previewFeed || busyId === String(previewFeed.feed_id) || atCap}
                title={atCap ? `At cap (${cap})` : "Subscribe to save & transcribe"}
              >
                {previewFeed && busyId === String(previewFeed.feed_id)
                  ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  : <Plus className="h-3 w-3 mr-1" />}
                Subscribe
              </Button>
            )}
          </div>
        ) : (
          <Tabs value={filter} onValueChange={(v) => setFilter(v as EpisodeFilter)}>
            <TabsList className="grid grid-cols-3 w-full">
              <TabsTrigger value="starred">Starred</TabsTrigger>
              <TabsTrigger value="archive">Archive</TabsTrigger>
              <TabsTrigger value="all">All</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-2 space-y-1">
          {isPreviewMode ? (
            <>
              {previewLoading && <div className="p-4 text-sm text-muted-foreground text-center">Loading episodes…</div>}
              {!previewLoading && previewEpisodes.length > 0 && (() => {
                const total = previewFeed?.episode_count ?? null;
                const shown = previewEpisodes.length;
                const reachedAll = total !== null && shown >= total;
                return (
                  <div className="px-2 pt-1 pb-2 border-b border-border/40 mb-1">
                    <div className="text-[11px] text-muted-foreground">
                      Showing {shown}{total !== null ? ` of ${total}` : ""} episodes
                    </div>
                    {total !== null && total > 50 && !reachedAll && (
                      <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                        Deep catalog — load more to search the full archive.
                      </div>
                    )}
                  </div>
                );
              })()}
              {!previewLoading && previewEpisodes.map((e) => {
                const key = e.guid ?? String(e.podcastindex_episode_id);
                const isSelected = selectedPreviewGuid === key;
                const row = previewSource === "subscribed" ? overlayFor(e) : null;
                const isRead = !!row?.is_read;
                const isStarred = !!row?.is_starred;
                const isArchived = !!row?.is_archived;
                return (
                  <button
                    key={key}
                    onClick={() => void selectPreviewEpisode(e)}
                    className={`w-full text-left p-2 rounded transition-shadow hover:ring-1 hover:ring-primary/40 ${isSelected ? "border-l-2 border-primary pl-1.5" : ""} ${isArchived ? "opacity-60" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {(e.image_url || previewFeed?.image_url) && (
                        <img src={e.image_url ?? previewFeed?.image_url ?? ""} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${isRead ? "text-muted-foreground" : "font-medium"}`}>{e.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {formatDate(e.published_at)} · {formatDuration(e.duration_seconds)}
                        </div>
                      </div>
                      {isStarred && <Star className="h-3 w-3 fill-current text-primary shrink-0" />}
                    </div>
                  </button>
                );
              })}
              {!previewLoading && previewEpisodes.length > 0 && (() => {
                const total = previewFeed?.episode_count ?? null;
                const shown = previewEpisodes.length;
                const reachedAll = total !== null && shown >= total;
                const atCeiling = previewPageSize >= 1000;
                if (reachedAll || atCeiling) {
                  return (
                    <div className="p-3 text-center text-[11px] text-muted-foreground">
                      {reachedAll ? `All ${total} episodes loaded` : "Reached load limit (1000)"}
                    </div>
                  );
                }
                return (
                  <div className="p-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={loadMorePreview}
                      disabled={previewLoadingMore}
                    >
                      {previewLoadingMore ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                      Load more episodes
                    </Button>
                  </div>
                );
              })()}
              {!previewLoading && previewEpisodes.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground text-center">No episodes returned by PodcastIndex.</div>
              )}
            </>
          ) : (
            <>
              {loading && <div className="p-4 text-sm text-muted-foreground text-center">Loading…</div>}
              {!loading && episodes.map((e) => {
                const sub = subs.find((s) => s.id === e.subscription_id);
                return (
                  <button
                    key={e.id}
                    onClick={() => setSelectedEpId(e.id)}
                    className={`w-full text-left p-2 rounded transition-shadow hover:ring-1 hover:ring-primary/40 ${selectedEpId === e.id ? "border-l-2 border-primary pl-1.5" : ""}`}
                  >
                    <div className="flex items-start gap-2">
                      {(e.image_url || sub?.image_url) && (
                        <img src={e.image_url ?? sub?.image_url ?? ""} alt="" className="h-10 w-10 rounded object-cover shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm truncate ${e.is_read ? "text-muted-foreground" : "font-medium"}`}>{e.title}</div>
                        <div className="text-xs text-muted-foreground truncate">
                          {sub?.title} · {formatDate(e.published_at)} · {formatDuration(e.duration_seconds)}
                        </div>
                      </div>
                      {e.is_starred && <Star className="h-3 w-3 fill-current text-primary shrink-0" />}
                    </div>
                  </button>
                );
              })}
              {!loading && episodes.length === 0 && (
                <div className="p-4 text-sm text-muted-foreground text-center">
                  {tab === "discover" ? "Select a podcast above to preview its episodes." : "No episodes here."}
                </div>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );

  const PlayerPane = (
    <div className="h-full flex flex-col">
      {playing ? (
        <>
          <div className="shrink-0 p-4 border-b border-border/60">
            <div className="flex items-start gap-3">
              {playing.image_url && (
                <img src={playing.image_url} alt="" className="h-16 w-16 rounded object-cover" />
              )}
              <div className="flex-1 min-w-0">
                <h2 className="text-base font-semibold leading-tight">{playing.title}</h2>
                <div className="text-xs text-muted-foreground mt-1">
                  {playing.sourceTitle} · {formatDate(playing.published_at)} · {formatDuration(playing.duration_seconds)}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <Button
                  size="sm"
                  variant={episodeLibraryItemId ? "ghost" : "outline"}
                  onClick={handlePromoteEpisode}
                  disabled={busyId === "promote-ep" || !active || !!episodeLibraryItemId}
                  title={episodeLibraryItemId ? "Already saved to My Library" : "Add this episode to My Library for citation"}
                >
                  {busyId === "promote-ep"
                    ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    : episodeLibraryItemId
                      ? <Check className="h-3 w-3 mr-1" />
                      : <BookPlus className="h-3 w-3 mr-1" />}
                  {episodeLibraryItemId ? "In My Library" : "Add to My Library"}
                </Button>
                {playing.kind === "subscribed" && selectedEp && (
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" onClick={() => handleEpisodeAction(selectedEp, { is_starred: !selectedEp.is_starred })}>
                      <Star className={`h-4 w-4 ${selectedEp.is_starred ? "fill-current text-primary" : ""}`} />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => handleEpisodeAction(selectedEp, { is_archived: !selectedEp.is_archived })}>
                      <Archive className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            <audio
              ref={audioRef}
              src={playing.audio_url}
              controls
              className="w-full mt-3"
              preload="none"
              onTimeUpdate={(e) => setAudioTime(e.currentTarget.currentTime)}
            />
          </div>
          <ScrollArea className="flex-1 min-h-0">
            <div className="p-4">
              {/* Transcript pane: saved > native (live) > loading > none */}
              {transcript || isNativeUnsaved ? (
                <div className="max-w-none space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold">Transcript</h3>
                    {transcript && transcript.model === "native" && (
                      <span className="text-[11px] text-muted-foreground">Saved · loaded from publisher</span>
                    )}
                  </div>
                  {isNativeUnsaved && (
                    <div className="rounded border border-primary/30 bg-primary/5 px-3 py-2 text-xs flex items-start gap-2">
                      <div className="flex-1">
                        Loaded live from the publisher and not saved. <strong>Save it</strong> to cite this episode or quote from it later.
                      </div>
                      <Button size="sm" variant="default" onClick={handleSaveNative} disabled={nativeSaving || !active}>
                        {nativeSaving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : null}
                        Save transcript
                      </Button>
                    </div>
                  )}
                  <div ref={transcriptContainerRef} className="relative">
                    {selection && (
                      <div
                        className="absolute z-10 -translate-x-1/2"
                        style={{ top: Math.max(selection.top, 0), left: selection.left }}
                      >
                        <Button
                          size="sm"
                          variant="default"
                          className="shadow-md"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={handleCreateExcerptFromSelection}
                          disabled={creatingExcerpt}
                        >
                          {creatingExcerpt
                            ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            : <Quote className="h-3 w-3 mr-1" />}
                          Create excerpt from selection
                        </Button>
                      </div>
                    )}
                    {transcriptWords ? (
                      <p className="text-sm leading-relaxed select-text">
                        {transcriptWords.map((w, i) => {
                          const active = audioTime >= w.start && audioTime < w.end;
                          return (
                            <span
                              key={i}
                              data-word-start={w.start}
                              onClick={() => seekTo(w.start)}
                              className={`cursor-pointer rounded px-0.5 transition-colors ${active ? "bg-primary/25 text-foreground font-medium" : "hover:bg-accent"}`}
                            >
                              {w.punctuated_word ?? w.word}{" "}
                            </span>
                          );
                        })}
                      </p>
                    ) : (
                      <div className="text-sm whitespace-pre-wrap select-text">{transcriptText}</div>
                    )}
                  </div>
                </div>
              ) : nativeLoading ? (
                <div className="space-y-3">
                  <div className="text-xs text-muted-foreground">Loading transcript from publisher…</div>
                  <div className="space-y-2 animate-pulse">
                    <div className="h-3 bg-muted rounded w-11/12" />
                    <div className="h-3 bg-muted rounded w-10/12" />
                    <div className="h-3 bg-muted rounded w-9/12" />
                    <div className="h-3 bg-muted rounded w-11/12" />
                    <div className="h-3 bg-muted rounded w-8/12" />
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {playing.description && (
                    <div
                      className="prose prose-sm max-w-none dark:prose-invert text-sm"
                      dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(playing.description ?? "") }}
                    />
                  )}
                  {selectedEp ? (
                    <div className="border border-dashed border-border rounded p-4 text-center space-y-2">
                      <div className="text-sm text-muted-foreground">No transcript available from the publisher.</div>
                      <Button onClick={handleTranscribe} disabled={transcribing} size="sm">
                        {transcribing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
                        Transcribe ({transcriptionCreditCost(selectedEp.duration_seconds)} credits)
                      </Button>
                    </div>
                  ) : (
                    <div className="border border-dashed border-border rounded p-4 text-center text-xs text-muted-foreground">
                      No publisher transcript found. Open the episode to materialize a row, then Transcribe to generate one with AI.
                    </div>
                  )}
                </div>
              )}
            </div>
          </ScrollArea>

        </>
      ) : (
        <div className="h-full flex items-center justify-center text-sm text-muted-foreground">
          {isPreviewMode ? "Select an episode to play." : "Select an episode to play."}
        </div>
      )}
    </div>
  );

  return (
    <>
      <AppSeo title="My Podcasts" />
      <div className="flex h-full w-full flex-col">
        <header className="shrink-0 border-b border-border/60 bg-background px-6 py-3 flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold flex items-center gap-2">
              <Mic className="h-5 w-5 text-primary" /> My Podcasts
            </h1>
            <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
              Subscribe to podcasts, listen in-browser, and transcribe episodes on demand. Use Audio Excerpts to clip moments into your Pieces.
            </p>
          </div>
        </header>

        {isMobile ? (
          <div className="flex-1 min-h-0 grid grid-rows-[auto_1fr_1fr]">
            <div className="h-72 border-b border-border/60">{LeftPane}</div>
            <div className="border-b border-border/60">{EpisodesPane}</div>
            <div>{PlayerPane}</div>
          </div>
        ) : (
          <ResizablePanelGroup direction="horizontal" className="flex-1 min-h-0">
            <ResizablePanel defaultSize={22} minSize={16}>{LeftPane}</ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={30} minSize={20}>{EpisodesPane}</ResizablePanel>
            <ResizableHandle withHandle />
            <ResizablePanel defaultSize={48} minSize={30}>{PlayerPane}</ResizablePanel>
          </ResizablePanelGroup>
        )}
      </div>
    </>
  );
};

export default MyPodcasts;
